import { CheckCircle2, Info, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';

type RowState = 'complete' | 'attention' | 'optional';

interface ChecklistRowProps {
  label: string;
  state: RowState;
  detail: string;
}

function ChecklistRow({ label, state, detail }: ChecklistRowProps): JSX.Element {
  const Icon = state === 'complete' ? CheckCircle2 : state === 'attention' ? TriangleAlert : Info;
  const icon_color =
    state === 'complete'
      ? 'text-emerald-600'
      : state === 'attention'
        ? 'text-amber-600'
        : 'text-muted-foreground';
  const pill_style =
    state === 'complete'
      ? 'bg-emerald-500/15 text-emerald-700'
      : state === 'attention'
        ? 'bg-amber-500/15 text-amber-700'
        : 'bg-muted text-muted-foreground';

  return (
    <li className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
      <span className="flex items-center gap-2 text-sm font-medium">
        <Icon className={`h-4 w-4 ${icon_color}`} aria-hidden />
        {label}
      </span>
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${pill_style}`}>{detail}</span>
    </li>
  );
}

interface CloseVisitValidationModalProps {
  anamnesis_complete: boolean;
  exam_complete: boolean;
  diagnoses_complete: boolean;
  plan_complete: boolean;
  needs_diagnosis_reason: boolean;
  needs_exam_reason: boolean;
  reason_diag: string;
  reason_exam: string;
  on_change_reason_diag: (v: string) => void;
  on_change_reason_exam: (v: string) => void;
  closing: boolean;
  error: string | null;
  on_confirm: () => void;
  on_cancel: () => void;
}

/** Replaces the always-visible inline close block: a checklist mirroring the backend's
 *  real close rules (diagnosis + exam), showing a justification field ONLY for whichever
 *  of those two is actually missing — never both, never unconditionally. */
export function CloseVisitValidationModal({
  anamnesis_complete,
  exam_complete,
  diagnoses_complete,
  plan_complete,
  needs_diagnosis_reason,
  needs_exam_reason,
  reason_diag,
  reason_exam,
  on_change_reason_diag,
  on_change_reason_exam,
  closing,
  error,
  on_confirm,
  on_cancel,
}: CloseVisitValidationModalProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="close-visit-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="bg-background flex w-full max-w-lg flex-col gap-4 rounded-lg border p-5 shadow-lg">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 id="close-visit-modal-title" className="text-base font-semibold">
              {t('clinical.close_modal_title')}
            </h2>
            <p className="text-muted-foreground text-sm">{t('clinical.close_modal_subtitle')}</p>
          </div>
        </div>

        <ul className="space-y-2">
          <ChecklistRow
            label={t('clinical.tab_anamnesis')}
            state={anamnesis_complete ? 'complete' : 'optional'}
            detail={anamnesis_complete ? t('clinical.checklist_complete') : t('clinical.checklist_pending')}
          />
          <ChecklistRow
            label={t('clinical.tab_exam')}
            state={exam_complete ? 'complete' : 'attention'}
            detail={
              exam_complete ? t('clinical.checklist_complete') : t('clinical.checklist_needs_exam_reason')
            }
          />
          <ChecklistRow
            label={t('clinical.tab_diagnoses')}
            state={diagnoses_complete ? 'complete' : 'attention'}
            detail={
              diagnoses_complete
                ? t('clinical.checklist_complete')
                : t('clinical.checklist_needs_diag_reason')
            }
          />
          <ChecklistRow
            label={t('clinical.tab_plan')}
            state={plan_complete ? 'complete' : 'optional'}
            detail={plan_complete ? t('clinical.checklist_complete') : t('clinical.checklist_pending')}
          />
          <ChecklistRow
            label={t('clinical.tab_prescriptions')}
            state="optional"
            detail={t('clinical.checklist_optional')}
          />
          <ChecklistRow
            label={t('clinical.tab_labs')}
            state="optional"
            detail={t('clinical.checklist_optional')}
          />
        </ul>

        {needs_diagnosis_reason ? (
          <div className="space-y-1">
            <Label className="text-xs">{t('clinical.close_no_diagnosis_reason')}</Label>
            <textarea
              className="border-input bg-background min-h-[70px] w-full rounded-md border px-3 py-2 text-sm"
              maxLength={500}
              value={reason_diag}
              onChange={(e) => on_change_reason_diag(e.target.value)}
            />
          </div>
        ) : null}

        {needs_exam_reason ? (
          <div className="space-y-1">
            <Label className="text-xs">{t('clinical.close_no_exam_reason')}</Label>
            <textarea
              className="border-input bg-background min-h-[70px] w-full rounded-md border px-3 py-2 text-sm"
              maxLength={500}
              value={reason_exam}
              onChange={(e) => on_change_reason_exam(e.target.value)}
            />
          </div>
        ) : null}

        {!needs_diagnosis_reason && !needs_exam_reason ? (
          <p className="bg-primary/5 text-muted-foreground flex items-center gap-2 rounded-md p-2 text-xs">
            <Info className="h-4 w-4 shrink-0" aria-hidden />
            {t('clinical.close_modal_no_reason_needed')}
          </p>
        ) : null}

        {error ? (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button type="button" variant="ghost" onClick={on_cancel} disabled={closing}>
            {t('clinical.close_modal_back')}
          </Button>
          <Button type="button" onClick={on_confirm} disabled={closing}>
            {t('clinical.close_visit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
